import React, { useState, useEffect } from 'react'
import { NavLink } from 'react-router-dom'

/**
 * Header. Phase 18-Z7 redesign (2026-05-19): premium nav with
 * gradient logo mark, scroll-aware background, hover underline.
 * data-cms-field attrs preserved for CMS inline editing.
 */
export default function Header({ content }) {
    const [open, setOpen] = useState(false)
    const [scrolled, setScrolled] = useState(false)
    const nav = content?.nav || []
    const siteName = content?.site?.name || ''
    const logoUrl = content?.site?.logoUrl || ''

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12)
        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    return (
        <header
            className={
                'sticky top-0 z-40 transition-all duration-300 ' +
                (scrolled
                    ? 'bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm'
                    : 'bg-white/80 backdrop-blur border-b border-transparent')
            }
        >
            <div className="container-fluid flex items-center justify-between h-18 py-3">
                <NavLink to="/" className="flex items-center gap-2.5">
                    {logoUrl ? (
                        <img data-cms-field="site.logoUrl" src={logoUrl} alt="logo" className="h-9 w-9 rounded-lg object-cover" />
                    ) : (
                        <span className="flex items-center justify-center h-9 w-9 rounded-lg bg-gradient-to-br from-brand-600 to-cyan-500 text-white font-extrabold text-lg shadow-md shadow-brand-500/30">
                            K
                        </span>
                    )}
                    <span data-cms-field="site.name" className="font-extrabold text-slate-900 tracking-tight text-[15px] md:text-base">
                        {siteName}
                    </span>
                </NavLink>
                <nav data-cms-field="nav" className="hidden md:flex items-center gap-9">
                    {nav.map((item, idx) => (
                        <NavLink
                            key={item.href || idx}
                            to={item.href || '/'}
                            data-cms-field={`nav.${idx}.label`}
                            className={({ isActive }) =>
                                'relative text-sm font-semibold transition-colors py-1 ' +
                                (isActive ? 'text-brand-600' : 'text-slate-700 hover:text-brand-600') +
                                ' after:absolute after:left-0 after:-bottom-0.5 after:h-0.5 after:bg-gradient-to-r after:from-brand-500 after:to-cyan-400 after:transition-all ' +
                                (isActive ? 'after:w-full' : 'after:w-0 hover:after:w-full')
                            }
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    className="md:hidden p-2 text-slate-700"
                    aria-label="menu"
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18M3 12h18M3 18h18" />
                    </svg>
                </button>
            </div>
            {open && (
                <nav className="md:hidden border-t border-slate-200 bg-white">
                    <div className="container-fluid py-2 flex flex-col">
                        {nav.map((item, idx) => (
                            <NavLink
                                key={item.href || idx}
                                to={item.href || '/'}
                                onClick={() => setOpen(false)}
                                data-cms-field={`nav.${idx}.label`}
                                className="py-2.5 text-slate-700 font-medium border-b border-slate-100 last:border-0"
                            >
                                {item.label}
                            </NavLink>
                        ))}
                    </div>
                </nav>
            )}
        </header>
    )
}
